<template>
    <box-view class="transaction-module" title="Quản lý Thu/Chi" :showBack="true" @close="$router.push('/admin')">
        <div class="container-fluid py-3">
            <!-- Action Buttons -->
            <div v-if="canManageTransactions" class="row mb-4">
                <div class="col-6">
                    <button class="btn btn-success btn-block btn-action" @click="openModal('income')">
                        <i class="fas fa-plus-circle mr-2"></i> Lập Phiếu Thu
                    </button>
                </div>
                <div class="col-6">
                    <button class="btn btn-danger btn-block btn-action" @click="openModal('expense')">
                        <i class="fas fa-minus-circle mr-2"></i> Lập Phiếu Chi
                    </button>
                </div>
            </div>

            <!-- Summary Cards -->
            <div class="row mb-4">
                <div class="col-md-4 mb-3">
                    <div class="summary-card income">
                        <div class="icon-wrapper">
                            <i class="fas fa-arrow-down"></i>
                        </div>
                        <div class="summary-content">
                            <div class="label">Tổng thu</div>
                            <div class="amount">{{ totalIncome.toLocaleString() }}₫</div>
                            <div class="mt-2 text-muted small border-top pt-1 text-left" style="font-size: 0.8em; line-height: 1.4;">
                                <div>💵 Tiền mặt: {{ totalCashIncome.toLocaleString() }}₫</div>
                                <div>🏦 Chuyển khoản: {{ totalTransferIncome.toLocaleString() }}₫</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="summary-card expense">
                        <div class="icon-wrapper">
                            <i class="fas fa-arrow-up"></i>
                        </div>
                        <div class="summary-content">
                            <div class="label">Tổng chi</div>
                            <div class="amount">{{ totalExpense.toLocaleString() }}₫</div>
                            <div class="mt-2 text-muted small border-top pt-1 text-left" style="font-size: 0.8em; line-height: 1.4;">
                                <div>💵 Tiền mặt: {{ totalCashExpense.toLocaleString() }}₫</div>
                                <div>🏦 Chuyển khoản: {{ totalTransferExpense.toLocaleString() }}₫</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="summary-card balance">
                        <div class="icon-wrapper">
                            <i class="fas fa-wallet"></i>
                        </div>
                        <div class="summary-content">
                            <div class="label">Quỹ hiện tại</div>
                            <div class="amount">{{ balance.toLocaleString() }}₫</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Transaction List -->
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 text-secondary">
                    <i class="fas fa-list mr-2"></i> Lịch sử giao dịch
                </h5>
            </div>

            <!-- Search Filter -->
            <div class="mb-3">
                <div class="row align-items-center">
                    <div class="col-12 col-md-4 mb-2 mb-md-0">
                        <div class="d-flex align-items-center h-100">
                             <div class="custom-control custom-checkbox custom-control-inline mr-3">
                                 <input type="checkbox" class="custom-control-input" id="filterIncome" v-model="showIncome">
                                 <label class="custom-control-label text-success font-weight-bold" for="filterIncome">Thiếu Thu (+)</label>
                             </div>
                             <div class="custom-control custom-checkbox custom-control-inline">
                                 <input type="checkbox" class="custom-control-input" id="filterExpense" v-model="showExpense">
                                 <label class="custom-control-label text-danger font-weight-bold" for="filterExpense">Phiếu Chi (-)</label>
                             </div>
                        </div>
                    </div>
                    <div class="col-6 col-md-2 mb-2 mb-md-0 pr-1">
                        <select class="form-control form-control-sm" v-model="selectedFolder" title="Thư mục">
                            <option value="">Tất cả thư mục</option>
                            <option v-for="folder in availableFolders" :key="folder" :value="folder">{{ folder }}</option>
                        </select>
                    </div>
                    <div class="col-6 col-md-2 mb-2 mb-md-0 pl-1">
                        <select class="form-control form-control-sm" v-model="selectedCreator" title="Người lập phiếu">
                            <option value="">Tất cả người lập</option>
                            <option v-for="creator in availableCreators" :key="creator" :value="creator">{{ creator }}</option>
                        </select>
                    </div>
                    <div class="col-12 col-md-4">
                        <div class="form-row">
                            <div class="col-4 col-md-4 pr-1">
                                <input type="date" class="form-control form-control-sm" v-model="startDate" placeholder="Từ">
                            </div>
                            <div class="col-4 col-md-4 px-1">
                                <input type="date" class="form-control form-control-sm" v-model="endDate" placeholder="Đến">
                            </div>
                            <div class="col-4 col-md-4 pl-1">
                                <div class="input-group input-group-sm">
                                     <input type="text" class="form-control border-right-0" v-model="searchQuery" placeholder="Tìm...">
                                     <div class="input-group-append">
                                        <span class="input-group-text bg-white border-left-0"><i class="fas fa-search text-muted"></i></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div v-for="trans in paginatedTransactions" :key="trans.id" class="col-12 col-md-6 mb-3">
                    <div class="transaction-card card border-0 shadow-sm" :class="trans.type">
                        <div class="card-body p-3">
                            <div class="d-flex justify-content-between align-items-start">
                                <div class="flex-grow-1">
                                    <div class="d-flex align-items-center mb-2">
                                        <span class="badge mr-2" :class="trans.type === 'income' ? 'badge-success' : 'badge-danger'">
                                            {{ trans.type === 'income' ? 'Thu' : 'Chi' }}
                                        </span>
                                        <small class="text-muted">{{ formatDate(trans.createdAt) }}</small>
                                    </div>
                                    <p class="mb-0 text-muted small">
                                        <span v-if="trans.folder" class="badge badge-info mr-1">{{ trans.folder }}</span>
                                        {{ trans.note }}
                                    </p>

                                    <!-- Action Buttons -->
                                    <div v-if="canManageTransactions" class="mt-2 pt-2 border-top d-flex gap-3">
                                        <span class="text-secondary small cursor-pointer" @click="printTransaction(trans)">
                                            <i class="fas fa-print mr-1"></i> In
                                        </span>
                                        <span class="text-primary small cursor-pointer" @click="editTransaction(trans)">
                                            <i class="fas fa-edit mr-1"></i> Sửa
                                        </span>
                                        <span class="text-danger small cursor-pointer" @click="confirmDelete(trans)">
                                            <i class="fas fa-trash-alt mr-1"></i> Xóa
                                        </span>
                                    </div>
                                </div>
                                <div class="transaction-amount text-right" :class="trans.type">
                                    <div class="font-weight-bold">{{ trans.type === 'income' ? '+' : '-' }}{{ trans.amount.toLocaleString() }}₫</div>
                                    <div v-show="trans.cash_amount > 0 || trans.transfer_amount > 0" class="small text-muted mt-1" style="font-size: 0.75em;">
                                        <div v-if="trans.cash_amount > 0">💵 {{ trans.cash_amount.toLocaleString() }}₫</div>
                                        <div v-if="trans.transfer_amount > 0">🏦 {{ trans.transfer_amount.toLocaleString() }}₫</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="filteredTransactions.length === 0" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-search fa-4x text-muted mb-3" v-if="searchQuery"></i>
                        <i class="fas fa-exchange-alt fa-4x text-muted mb-3" v-else></i>
                        <h5 class="text-muted">{{ searchQuery ? 'Không tìm thấy kết quả phù hợp' : 'Chưa có giao dịch' }}</h5>
                    </div>
                </div>
            </div>

            <!-- PAGINATION -->
            <nav v-if="totalPages > 1" class="mt-3">
                <ul class="pagination pagination-sm justify-content-center flex-wrap">
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" href="#" @click.prevent="goToPage(1)" title="Trang đầu">««</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === 1 }">
                        <a class="page-link" href="#" @click.prevent="goToPage(currentPage - 1)">«</a>
                    </li>
                    <li v-for="(page, idx) in visiblePages" :key="'p'+idx"
                        class="page-item"
                        :class="{ active: page === currentPage, disabled: page === '...' }">
                        <a class="page-link" href="#"
                           @click.prevent="page !== '...' && goToPage(page)">{{ page }}</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" href="#" @click.prevent="goToPage(currentPage + 1)">»</a>
                    </li>
                    <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                        <a class="page-link" href="#" @click.prevent="goToPage(totalPages)" title="Trang cuối">»»</a>
                    </li>
                </ul>
                <div class="text-center text-muted small">Trang {{ currentPage }} / {{ totalPages }}</div>
            </nav>
        </div>

        <!-- Modal -->
        <van-dialog v-model="showModal" :title="isEditing ? 'Cập nhật Phiếu' : (form.type === 'income' ? 'Phiếu Thu' : 'Phiếu Chi')" show-cancel-button @confirm="save">
            <div class="p-3">
                <div class="form-group" v-if="isEditing">
                    <label class="font-weight-bold"><i class="fas fa-exchange-alt mr-1"></i> Loại phiếu</label>
                    <select class="form-control" v-model="form.type">
                         <option value="income">Phiếu Thu (+)</option>
                         <option value="expense">Phiếu Chi (-)</option>
                    </select>
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-dollar-sign mr-1"></i> Số tiền</label>
                    <div class="row">
                        <div class="col-6 pr-1">
                            <label class="small text-muted mb-1">💵 Tiền mặt</label>
                            <money-input v-model="form.cash_amount" />
                        </div>
                        <div class="col-6 pl-1">
                            <label class="small text-muted mb-1">🏦 Chuyển khoản</label>
                            <money-input v-model="form.transfer_amount" />
                        </div>
                    </div>
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-folder mr-1"></i> Thư mục (Tuỳ chọn)</label>
                    <input type="text" class="form-control" v-model="form.folder" list="folderOptions" placeholder="Nhập hoặc chọn tên thư mục">
                    <datalist id="folderOptions">
                        <option v-for="folder in availableFolders" :key="folder" :value="folder"></option>
                    </datalist>
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-sticky-note mr-1"></i> Lý do / Mô tả</label>
                    <input type="text" class="form-control" v-model="form.note">
                </div>
            </div>
        </van-dialog>
    </box-view>
</template>

<script>
export default {
    name: 'Transaction',
    props: {
        transactions: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            default: () => ({})
        },
        settings: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            showModal: false,
            isEditing: false,
            searchQuery: '',
            startDate: '',
            endDate: '',
            showIncome: true,
            showExpense: true,
            selectedFolder: '',
            selectedCreator: '',
            form: {
                id: null,
                type: 'income',
                amount: 0,
                cash_amount: 0,
                transfer_amount: 0,
                folder: '',
                note: '',
                createdAt: ''
            },
            currentPage: 1,
            pageSize: 30
        };
    },
    computed: {
        totalIncome() {
            return this.filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
        },
        totalCashIncome() {
            return this.filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (parseFloat(t.cash_amount) || 0), 0);
        },
        totalTransferIncome() {
            return this.filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + (parseFloat(t.transfer_amount) || 0), 0);
        },
        totalExpense() {
            return this.filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);
        },
        totalCashExpense() {
            return this.filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (parseFloat(t.cash_amount) || 0), 0);
        },
        totalTransferExpense() {
            return this.filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + (parseFloat(t.transfer_amount) || 0), 0);
        },
        balance() {
            return this.totalIncome - this.totalExpense;
        },
        availableFolders() {
            const folders = new Set();
            this.transactions.forEach(t => {
                if (t.folder) folders.add(t.folder);
            });
            return Array.from(folders).sort();
        },
        availableCreators() {
            const creators = new Set();
            this.transactions.forEach(t => {
                if (t.createdBy) creators.add(t.createdBy);
            });
            return Array.from(creators).sort();
        },
        filteredTransactions() {
            let result = this.transactions;

            // Filter by type (Income / Expense)
            if (!this.showIncome && !this.showExpense) {
                return []; // Show nothing if both are unchecked
            } else if (this.showIncome && !this.showExpense) {
                result = result.filter(t => t.type === 'income');
            } else if (!this.showIncome && this.showExpense) {
                result = result.filter(t => t.type === 'expense');
            }

            // Filter by search query
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                result = result.filter(t => 
                    (t.note && t.note.toLowerCase().includes(query)) ||
                    (t.folder && t.folder.toLowerCase().includes(query)) ||
                    (t.amount && t.amount.toString().includes(query))
                );
            }

            // Filter by folder
            if (this.selectedFolder) {
                result = result.filter(t => t.folder === this.selectedFolder);
            }

            // Filter by creator
            if (this.selectedCreator) {
                result = result.filter(t => t.createdBy === this.selectedCreator);
            }

            // Filter by date range (compare date strings to avoid timezone issues)
            if (this.startDate) {
                result = result.filter(t => {
                    const tDate = (t.createdAt || '').substring(0, 10); // "YYYY-MM-DD"
                    return tDate >= this.startDate;
                });
            }
            if (this.endDate) {
                result = result.filter(t => {
                    const tDate = (t.createdAt || '').substring(0, 10); // "YYYY-MM-DD"
                    return tDate <= this.endDate;
                });
            }

            return result;
        },
        canManageTransactions() {
            const role = this.user.role || 'admin';
            if(role === 'admin') return true;
            const roleConfig = this.settings.user?.roles?.[role];
            return roleConfig?.permissions?.includes('manageTransactions');
        },
        totalPages() {
            return Math.ceil(this.filteredTransactions.length / this.pageSize);
        },
        visiblePages() {
            const total = this.totalPages;
            const current = this.currentPage;
            const delta = 2;
            const pages = [];
            if (total <= 7) {
                for (let i = 1; i <= total; i++) pages.push(i);
                return pages;
            }
            pages.push(1);
            const rangeStart = Math.max(2, current - delta);
            const rangeEnd = Math.min(total - 1, current + delta);
            if (rangeStart > 2) pages.push('...');
            for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
            if (rangeEnd < total - 1) pages.push('...');
            pages.push(total);
            return pages;
        },
        paginatedTransactions() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredTransactions.slice(start, start + this.pageSize);
        }
    },
    methods: {
        goToPage(page) {
            if (page < 1) page = 1;
            if (page > this.totalPages) page = this.totalPages;
            this.currentPage = page;
        },
        formatDate(isoString) {
            if (!isoString) return '';
            const date = new Date(isoString);
            const time = date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
            const day = date.toLocaleDateString('vi-VN');
            return `${time} ${day}`;
        },
        openModal(type) {
            this.isEditing = false;
            
            this.form = {
                id: null,
                type: type,
                amount: 0,
                cash_amount: 0,
                transfer_amount: 0,
                folder: '',
                note: '',
                createdAt: new Date().toISOString()
            };
            this.showModal = true;
        },
        editTransaction(trans) {
            this.isEditing = true;
            this.form = {
                id: trans.id,
                type: trans.type,
                amount: trans.amount,
                cash_amount: trans.cash_amount || 0,
                transfer_amount: trans.transfer_amount || 0,
                folder: trans.folder || '',
                note: trans.note,
                createdAt: trans.createdAt || ''
            };
            this.showModal = true;
        },
        confirmDelete(trans) {
            const label = trans.type === 'income' ? 'Phiếu Thu' : 'Phiếu Chi';
            this.$root.confirm(`Bạn có chắc chắn muốn xóa ${label} trị giá ${trans.amount.toLocaleString()}₫ không?`, 'Xác nhận xóa').then(() => {
                this.$emit('delete-transaction', trans.id);
            });
        },
        printTransaction(trans) {
            this.$emit('print-transaction', trans);
        },
        save() {
            // Auto sum the amount
            const total = (Number(this.form.cash_amount) || 0) + (Number(this.form.transfer_amount) || 0);
            if (total > 0) {
                this.form.amount = total;
            }

            if (!this.form.amount || this.form.amount <= 0) {
                this.$toast?.fail('Vui lòng nhập số tiền hợp lệ');
                return;
            }

            this.$emit('add-transaction', this.form);
            this.showModal = false;
        }
    }
};
</script>

